import { defineStore } from 'pinia';

import { Observation } from "@/models/Observation.model";
import { Pupil } from "@/models/Pupil.model";
import { useClassroomsStore } from "@/stores/Classrooms.store";
import SmaSyLocalStorage from "@/utils/SmaSyLocalStorage.util";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { ObservationFilterDef } from "@/stores/filters/ObservationFilterDef";
import { AbstractBaseStore, AbstractStoreState, Method } from "@/stores/AbstractBase.store";
import { useDefaultOrderStore } from './DefaultOrder.store';

interface ObservationStoreState extends AbstractStoreState<Observation, ObservationFilterDef> {

}

const BaseStore = AbstractBaseStore<Observation, ObservationStoreState, ObservationFilterDef>();

const forks = new Map<string, any>();
export function forkedObservationsStore(forkName: string) {

	// Check if the store is already created
	if (!forks.has(forkName)) {
		forks.set(forkName, defineStore(forkName, {

			/**
			 * Return the initial state of the store.
			 * @returns ObservationStoreState
			 */
			state: (): ObservationStoreState => {

				// Set the default filter
				const defaultFilter = new ObservationFilterDef();
				let interval = useOrganisationsStore().currentOrganisation?.getCurrentSchoolYearInterval();
				if (interval) {
					defaultFilter.setInterval(interval);
				}

				// Scope to the current classroom by default. Views that want a different
				// scope (e.g. school-wide) set classroomIds on the filter themselves.
				const currentClassroom = useClassroomsStore().currentClassroom;
				defaultFilter.classroomIds = currentClassroom?.id ? [currentClassroom.id] : [];

				// Load the last known filter from local storage
				const storedFilterData = SmaSyLocalStorage.getCurrentUserItem(forkName +'-filters');
				let initialFilter: ObservationFilterDef | null = null;
				if (storedFilterData) {
					initialFilter = (new ObservationFilterDef).fromJson(storedFilterData);
					// Classroom scope is view context, not a stored user choice; always
					// take it from the current context, never from the persisted filter.
					initialFilter.classroomIds = defaultFilter.classroomIds;
				}

				// Base state from the AbstractBaseStore
				return BaseStore.state({

					defaultFilter,
					initialFilter,
					order: useDefaultOrderStore().observationsOrder,

				});

			},

			getters: {

				...BaseStore.getters,

				apiPath(): string {
					return 'observations';
				},

				defaultMask() {
					return [
						'*',
						'files.*',
						'files.variations.*',
						'classroom.id',
						'classroom.name',
						'author.*',
						'pupil.*',
						'pupil.classrooms.id',
						'pupil.classrooms.icon.*',
						'pupil.classrooms.symbol',
						'pupil.classrooms.color',
						'pupilActivity.*',
						'lesson.*',
						'lessonItem.*',
						'lessonItem.lessonPlan.*',
						'pupilActivity.activity.*',
						'pupilActivity.lessonItem.*',
						'pupilActivity.lessonItem.icon.*',
						'pupilActivity.lessonItem.lesson.*',
						'pupilActivity.lessonItem.activity.*',
						'pupilActivity.lessonItem.lessonPlan.*',
						'lessonItem.lessonPlan.goals.*',
						'pupilActivity.lessonItem.lessonPlan.goals.*',
					];
				},

			},

			actions: {

				...BaseStore.actions,

				mapToItem(data: any): Observation {
					return Observation.createFromApiData(data);
				},

				new(pupil: Pupil) {
					let newObservation = new Observation(pupil);

					const currentClassroom = useClassroomsStore().currentClassroom;
					if (!currentClassroom) {
						throw new Error('No current classroom set');
					}

					newObservation.classroomId = currentClassroom.id;
					newObservation.note = "";

					return newObservation;
				},

				applyFilter(filter: ObservationFilterDef, store: boolean = true) {

					if (store) {
						SmaSyLocalStorage.setCurrentUserItem(forkName +'-filters', filter, 60 * 60 * 8);
					}

					return this.superApplyFilter(filter);

				},

				resetFilter(store: boolean = true) {

					if (store) {
						SmaSyLocalStorage.removeCurrentUserItem(forkName +'-filters');
					}

					return this.superResetFilter();

				},

				async loadCompletely(observation: Observation) {

					const response = await this.apiClient.get(this.apiUrl(Method.VIEW, observation.id), {
						params: {
							mask: [
								this.defaultMask,

								'lessonItem.lessonPlan.*',
								'pupilActivity.lessonItem.lessonPlan.*',
								'pupilActivity.lessonItem.lessonPlan.goals.*',
							].join(','),
						}
					});

					observation.hydrateWithApiData(response.data.data);
					return observation;
				},

				updateStoreItemFromNewItem(existingModel: Observation, newData: Observation) {
					existingModel.setFromModel(newData);
				}

			}

		}));

	}

	return forks.get(forkName);
}

/**
 * Default observations store
 */
export const useObservationsStore = forkedObservationsStore('observations');

/**
 * Specific observations store
 */
export const useForkedObservationsStore = (forkName: string) => {
	return forkedObservationsStore(forkName);
}
